<script setup lang="ts">
import { moneyProportion, type Money, zeroMoney } from '@taler/domain'
import { computed } from 'vue'
import { hasProgressTreatment } from '../internal/presentation.ts'
import type { Target } from '../internal/types.ts'

const props = defineProps<{ target: Target }>()

/** Only for a money metric with one lower bound: how far the current amount has come toward it. */
const fraction = computed(() => {
  if (!hasProgressTreatment(props.target)) return undefined
  const current = props.target.evaluation.currentValue
  const minimum = props.target.minValue
  if (current?.kind !== 'money' || minimum?.kind !== 'money') return undefined
  const goal: Money = minimum.value
  const proportion = moneyProportion(current.value, zeroMoney(goal.currency), goal)
  return Math.min(1, Math.max(0, proportion))
})
</script>

<template>
  <div v-if="fraction !== undefined" class="flex items-center gap-[var(--space-cell)]">
    <div
      aria-hidden="true"
      class="relative h-2.5 flex-1 overflow-hidden rounded-full bg-[color:var(--color-surface-sunken)]"
    >
      <span
        class="absolute inset-y-0 left-0 rounded-full bg-[color:var(--color-accent)]"
        :style="{ width: `${fraction * 100}%` }"
      />
    </div>
    <span
      class="w-12 shrink-0 text-right text-[length:var(--text-step-2)] font-semibold tabular-nums text-[color:var(--color-text-muted)]"
    >
      {{ Math.round(fraction * 100) }}%
    </span>
  </div>
</template>
